import {
  ExpenseDraft,
  ExpenseDraftFormValue,
  OperationFlags,
} from "@/pages/Expenses/RecordExpense/types/expenseDraft";
import { Button, Flex, message, Modal } from "antd";
import { Form } from "antd";
import { ExpenseType } from "@/types/expenseType";
import { ExpenseDescription } from "@/types/expenseDescription";
import { Supplier } from "@/types/supplier";
import { useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import { UseQueryResult } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useGetInputs } from "@/pages/Expenses/RecordExpense/hooks/useExpenseDraftForm";
import {
  getFlag,
  getModalConfig,
} from "@/pages/Expenses/RecordExpense/utils/expenseDraftModal";
import { PREFIX } from "@/pages/Expenses/RecordExpense/constants";
import useExpenseDescription from "@/pages/Expenses/RecordExpense/hooks/useExpenseDescription";
import useExpenseSupplier from "@/pages/Expenses/RecordExpense/hooks/useExpenseSupplier";
import { useUpdateExpenseDraft } from "@/pages/Expenses/RecordExpense/hooks/useExpenseDrafts";
import { LabeledValue } from "antd/es/select";
import { UpdateExpenseDescriptionFormValues } from "@/pages/Expenses/ManageExpense/api/expenseDescriptions";
import { useUpdateExpenseDescription } from "@/pages/Expenses/ManageExpense/hooks/useExpenseDescriptions";

interface ExpenseDraftEditModalProps {
  data?: ExpenseDraft;
  expenseTypes: ExpenseType[];
  descriptions: UseQueryResult<ExpenseDescription[], Error>;
  suppliers: UseQueryResult<Supplier[], Error>;
  isOpen: boolean;
  onClose: () => void;
}

interface FormValues {
  date: Dayjs;
  description_id?: string | LabeledValue;
  supplier_id?: string | LabeledValue;
  price: number;
  quantity?: number;
  unit_id?: string;
  staff_name?: string;
}

const ExpenseDraftEditModal = ({
  data,
  expenseTypes,
  descriptions,
  suppliers,
  isOpen,
  onClose,
}: ExpenseDraftEditModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);

  // Used to store updates for the expense description, specifically when the description's unit_id is not set and needs to be updated
  const [descriptionUpdates, setDescriptionUpdates] =
    useState<UpdateExpenseDescriptionFormValues>();

  const expenseType =
    expenseTypes.find((type) => type.id === data?.expense_type_id) || undefined;

  const inputs = useGetInputs({
    form,
    expenseType,
    setDescriptionUpdates,
  });

  const { mutate: updateExpenseDescription } = useUpdateExpenseDescription();
  const { handleAddExpenseDescription, handleReactivateExpenseDescription } =
    useExpenseDescription();
  const { handleAddExpenseSupplier, handleReactivateExpenseSupplier } =
    useExpenseSupplier();

  const { mutate: updateDraft } = useUpdateExpenseDraft();

  // handle updating expense to drafts with the flags
  const handleUpdateToDrafts = async (
    values: ExpenseDraftFormValue,
    flags: OperationFlags
  ) => {
    const onCloseConfirmatioModal = () => {
      setSubmitting(false);
      Modal.destroyAll();
    };

    // Create a working copy of values for processing
    let workingValues = { ...values };

    if (flags.isNewDescription) {
      await handleAddExpenseDescription({
        values: workingValues,
        onSuccess: (description) => {
          workingValues.description_id = description.id;
          descriptions.refetch();
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    } else if (flags.isReactivateDescription) {
      await handleReactivateExpenseDescription({
        values: workingValues,
        onSuccess: (description) => {
          workingValues.description_id = description.id;
          descriptions.refetch();
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    }

    if (flags.isNewSupplier) {
      await handleAddExpenseSupplier({
        values: workingValues,
        onSuccess: (supplier) => {
          workingValues.supplier_id = supplier.id;
          suppliers.refetch();
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    } else if (flags.isReactivateSupplier) {
      await handleReactivateExpenseSupplier({
        values: workingValues,
        onSuccess: (supplier) => {
          workingValues.supplier_id = supplier.id;
          suppliers.refetch();
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    }

    if (descriptionUpdates) {
      updateExpenseDescription(descriptionUpdates, {
        onSuccess: () => {
          message.success("Successfully updated expense description");
          onCloseConfirmatioModal();
        },
        onError: () => {
          onCloseConfirmatioModal();
        },
      });
    }

    // Update draft via API
    if (data?.id) {
      updateDraft(
        { id: data.id, payload: workingValues },
        {
          onSuccess: () => {
            form.resetFields();
            setSubmitting(false);
            message.success("Successfully updated expense draft");
            onClose();
          },
          onError: () => {
            setSubmitting(false);
          },
        }
      );
    }
  };

  const handleFinish = async (values: FormValues) => {
    setSubmitting(true);

    const description_id =
      typeof values.description_id === "string"
        ? values.description_id
        : values.description_id?.value.toString();
    const supplier_id =
      typeof values.supplier_id === "string"
        ? values.supplier_id
        : values.supplier_id?.value.toString();

    const newValues: ExpenseDraftFormValue = {
      ...values,
      description_id: description_id,
      supplier_id: supplier_id,
      expense_type_id: data?.expense_type_id || "",
      user_id: data?.user_id as number,
      date: values.date.toISOString(),
    };

    const flags: OperationFlags = {
      isNewDescription: getFlag(newValues.description_id, PREFIX.NEW),
      isNewSupplier: getFlag(newValues.supplier_id, PREFIX.NEW),
      isReactivateDescription: getFlag(
        newValues.description_id,
        PREFIX.INACTIVE
      ),
      isReactivateSupplier: getFlag(newValues.supplier_id, PREFIX.INACTIVE),
    };

    const modalConfig = getModalConfig(flags);

    try {
      if (modalConfig) {
        Modal.confirm({
          title: modalConfig.title,
          content: modalConfig.content,
          okText: "Yes",
          cancelText: "Cancel",
          onOk: () => handleUpdateToDrafts(newValues, flags),
          onCancel: () => setSubmitting(false),
        });
      } else {
        handleUpdateToDrafts(newValues, flags);
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen && data) {
      const isDescriptionActive = data?.description?.is_active;
      const isSupplierActive = data?.supplier?.is_active;
      const isUnitActive = data?.unit?.is_active;

      const description_id = isDescriptionActive
        ? data?.description_id
        : {
            key: `${PREFIX.INACTIVE}${data?.description_id}`,
            value: `${PREFIX.INACTIVE}${data?.description_id}`,
            label: data?.description?.name,
          };
      const supplier_id = isSupplierActive
        ? data?.supplier_id
        : {
            key: `${PREFIX.INACTIVE}${data?.supplier_id}`,
            value: `${PREFIX.INACTIVE}${data?.supplier_id}`,
            label: data?.supplier?.name,
          };
      const unit_id = isUnitActive ? data?.unit_id : undefined;

      form.setFieldsValue({
        date: dayjs(data?.date),
        description_id: description_id,
        supplier_id: supplier_id,
        price: data?.price,
        quantity: data?.quantity,
        unit_id: unit_id,
        staff_name: data?.staff_name,
      });
      return;
    }

    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, data]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title="Edit Expense Draft"
      footer={null}
      centered
      destroyOnHidden
    >
      <Form<FormValues> form={form} onFinish={handleFinish} layout="vertical">
        {inputs.map((input) => (
          <Form.Item
            {...input.itemProps}
            labelCol={{ style: { paddingBottom: 4 } }}
            key={input.itemProps.name}
          >
            {input.component}
          </Form.Item>
        ))}

        <Flex justify="flex-end" gap={16} align="center">
          <Button variant="filled" onClick={onClose}>
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="default"
            variant="solid"
            color="primary"
            loading={submitting}
          >
            Update Expense
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};

export default ExpenseDraftEditModal;
